import React, { useState } from "react";
import { Box, TextField, Button, Typography, Paper, InputAdornment, IconButton, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { AccountCircle, Visibility, VisibilityOff, Lock, Person } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const Register = () => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [role, setRole] = useState("partner");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const navigate = useNavigate();
    const API_BASE_URL = process.env.REACT_APP_API_URL;

    const handleRegister = async () => {
        setError(null);
        setSuccess(null);

        if (!name || !email || !password) {
            setError("All fields are required.");
            return;
        }

        if (!/\S+@\S+\.\S+/.test(email)) {
            setError("Invalid email format.");
            return;
        }

        if (password.length < 6) {
            setError("Password must be at least 6 characters long.");
            return;
        }

        try {
            const response = await axios.post(`${API_BASE_URL}/auth/register`, {
                name,
                email,
                password,
                role,
            });

            if (response.status === 201) {
                setSuccess("Registration successful! Redirecting...");
                setTimeout(() => navigate("/login"), 2000);
            }
        } catch (error) {
            setError(error.response?.data?.message || "Registration failed. Please try again.");
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #6e7864, #53574f)",
                padding: "20px"
            }}
        >
            <Paper elevation={10} sx={{ padding: 5, borderRadius: 3, maxWidth: 400, textAlign: "center", background: "linear-gradient(135deg, #6e7864, #53574f)", color: "#FFFFFF" }}>
                {/* Logo */}
                <img src="/logo.png" alt="NuGreen Logo" style={{ height: "150px", marginBottom: "20px" }} />

                {/* Title */}
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: "#D0D0D0" }}>
                    Create an Account
                </Typography>

                {/* Error Message */}
                {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
                {success && <Typography color="success" sx={{ mb: 2 }}>{success}</Typography>}

                {/* Full Name Input */}
                <TextField
                    fullWidth
                    label="Full Name"
                    variant="outlined"
                    margin="normal"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Person />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#B0B0B0" },
                            "&:hover fieldset": { borderColor: "#A0A0A0" },
                            "&.Mui-focused fieldset": { borderColor: "#909090" },
                        },
                    }}
                />

                {/* Email Input */}
                <TextField
                    fullWidth
                    label="Email"
                    variant="outlined"
                    margin="normal"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <AccountCircle />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#B0B0B0" },
                            "&:hover fieldset": { borderColor: "#A0A0A0" },
                            "&.Mui-focused fieldset": { borderColor: "#909090" },
                        },
                    }}
                />

                {/* Password Input */}
                <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    margin="normal"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Lock />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#B0B0B0" },
                            "&:hover fieldset": { borderColor: "#A0A0A0" },
                            "&.Mui-focused fieldset": { borderColor: "#909090" },
                        },
                    }}
                />

                {/* Role Selection */}
                <FormControl fullWidth margin="normal" sx={{
                    "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#B0B0B0" },
                        "&:hover fieldset": { borderColor: "#A0A0A0" },
                        "&.Mui-focused fieldset": { borderColor: "#909090" },
                    },
                }}>
                    <InputLabel>Role</InputLabel>
                    <Select value={role} onChange={(e) => setRole(e.target.value)}>
                        <MenuItem value="partner">Partner</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                    </Select>
                </FormControl>

                {/* Register Button */}
                <Button
                    fullWidth
                    variant="contained"
                    sx={{
                        marginTop: "20px",
                        padding: "12px",
                        fontWeight: "bold",
                        background: "linear-gradient(135deg, #8aa172, #6e7864)"
                    }}
                    onClick={handleRegister}
                >
                    Register
                </Button>
            </Paper>
        </Box>
    );
};

export default Register;
